/** Palet pastel terpadu untuk semua stage (gaya low-poly outdoor).
 *  Titik tengah yang disetel manual: cukup berwarna/ceria (tidak pucat),
 *  tanpa kembali ke neon/menyala (iterasi: terlalu cerah -> terlalu pucat). */

export const PALETTE = {
  grassMint: 0xa9d8b4,
  grassSage: 0x9cbfa3,
  groundOuter: 0xbcdcc5,
  skyTop: 0x9fceea,
  skyHorizon: 0xf7ead0,
  fog: 0xdfe7ec,
  glass: 0xe0edf2,
  frameCream: 0xf7efdc,
  framePillar: 0xf1e7cf,
  trunkCream: 0xe2cfa8,
  trunkDeep: 0xcfb990,
  canopyMint: 0xa9dcb6,
  canopySage: 0x9cc3a6,
  canopyTosca: 0x9fd4c2,
  rockLavender: 0xc3bcd8,
  rockLight: 0xd2ccdf,
  petalPink: 0xf3b8c1,
  petalYellow: 0xf1dd9f,
  petalLilac: 0xd9c7ec,
  petalWhite: 0xf7f7f7,
  center: 0xf2c975,
  /** Aksen finish: turquoise pastel (cukup jenuh, tetap pastel). */
  accentTurquoise: 0x6fd1b7,
  /** Aksen finish: coral pastel. */
  accentCoral: 0xef93a0,
} as const;

export interface StageTheme {
  skyGlow: number;
  planeSky: number;
  platform: number;
  platformAlt: number;
  ramp: number;
  crate: number;
  crateAccent: number;
  accent: number;
}

/** Tema per stage: satu keluarga pastel, hanya posisi aksen bergeser. */
export const THEMES: Record<string, StageTheme> = {
  meadow: {
    skyGlow: 0xbfe3f2,
    planeSky: 0x8ecae6,
    platform: 0x8ecae6,
    platformAlt: 0xa99adb,
    ramp: 0xdfcfa0,
    crate: 0xf2c1a0,
    crateAccent: 0xef93a0,
    accent: 0x6fd1b7,
  },
  lavender: {
    skyGlow: 0xd3c9ea,
    planeSky: 0xa89ad6,
    platform: 0xa89ad6,
    platformAlt: 0x8ecae6,
    ramp: 0xdfcfa0,
    crate: 0xf2c1a0,
    crateAccent: 0xef93a0,
    accent: 0x6fd1b7,
  },
  dune: {
    skyGlow: 0xf1e2b8,
    planeSky: 0xdfcfa0,
    platform: 0x8ecae6,
    platformAlt: 0xddcfae,
    ramp: 0xdfcfa0,
    crate: 0xe5d284,
    crateAccent: 0x6fd1b7,
    accent: 0x6fd1b7,
  },
  pole: {
    skyGlow: 0xbfdaea,
    planeSky: 0x8ecae6,
    platform: 0x8ecae6,
    platformAlt: 0x9cbed6,
    ramp: 0xdfcfa0,
    crate: 0xbbaed6,
    crateAccent: 0xef93a0,
    accent: 0x6fd1b7,
  },
  stagehall: {
    skyGlow: 0xf2d7bd,
    planeSky: 0xf2c1a0,
    platform: 0xf2c1a0,
    platformAlt: 0x8ecae6,
    ramp: 0xdfcfa0,
    crate: 0x9fd4c2,
    crateAccent: 0xef93a0,
    accent: 0x6fd1b7,
  },
  loop: {
    skyGlow: 0xd3c9ea,
    planeSky: 0xa89ad6,
    platform: 0xa89ad6,
    platformAlt: 0x8ecae6,
    ramp: 0xdfcfa0,
    crate: 0xef93a0,
    crateAccent: 0xf2b59c,
    accent: 0x6fd1b7,
  },
  glacier: {
    skyGlow: 0xc3e2ef,
    planeSky: 0x9ecbe2,
    platform: 0x9ecbe2,
    platformAlt: 0xe8e0cc,
    ramp: 0xdccfb9,
    crate: 0xe5d284,
    crateAccent: 0x6fd1b7,
    accent: 0x6fd1b7,
  },
  dash: {
    skyGlow: 0xf0dcb8,
    planeSky: 0xefc98f,
    platform: 0xefc98f,
    platformAlt: 0x8ecae6,
    ramp: 0xdfcfa0,
    crate: 0xe5d284,
    crateAccent: 0xef93a0,
    accent: 0xef93a0,
  },
  garden: {
    skyGlow: 0xd3c9ea,
    planeSky: 0xa89ad6,
    platform: 0x8ecae6,
    platformAlt: 0xf2c1a0,
    ramp: 0xdfcfa0,
    crate: 0xef93a0,
    crateAccent: 0xf2b59c,
    accent: 0x6fd1b7,
  },
  river: {
    skyGlow: 0xc3e2ef,
    planeSky: 0x8ecae6,
    platform: 0x8ecae6,
    platformAlt: 0xe6cfa4,
    ramp: 0xe2d6bc,
    crate: 0xe9dc9e,
    crateAccent: 0x6fd1b7,
    accent: 0x6fd1b7,
  },
};

export const DEFAULT_THEME = 'meadow';

export function resolveTheme(name: string | undefined): StageTheme {
  return THEMES[name ?? DEFAULT_THEME] ?? THEMES[DEFAULT_THEME];
}